/* eslint-disable jsx-a11y/no-autofocus */
import React, { Dispatch } from 'react';

import { Menu as MUIMenu } from '@mui/material';
import { ContextMenu } from '.';
import { Node, XYPosition } from 'reactflow';
import { Connector, CreateNode } from '..';

import { NodesAutocomplete } from '@common/Autocomplete/Nodes';
import { useTheme } from '../../../styles';
import { useSelector } from 'react-redux';
import { showTelnyxFeatures, State } from '@store/index';
import { ListItemType } from '@common/Autocomplete/Base';
import { useModules } from '@common/hooks/useModules';

interface Props {
  nodes: Node[];
  contextMenu: ContextMenu | null;
  setContextMenu: Dispatch<ContextMenu | null>;
  addNode: (params: CreateNode) => void;
  setConnector: React.Dispatch<Connector>;
}

export const Menu: React.FC<Props> = ({ contextMenu, setContextMenu, addNode, setConnector }) => {
  const isTelnyxUser = useSelector((state: State) => showTelnyxFeatures(state));

  const commands = useSelector((state: State) => state.tabs.active.commands);
  const position: XYPosition = { x: contextMenu?.mouseX as number, y: contextMenu?.mouseY as number };
  const anchorPosition = contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined;

  const { modules } = useModules();

  const themeType = useSelector((state: State) => state.flow.theme);

  const theme = useTheme();
  const colors = theme.telnyx;

  const trigger = commands.find((command) => command.config.trigger);
  const hasModuleNode = commands.find((command) => command.config.command_id === 'node.module');

  const options = [
    !trigger &&
      contextMenu?.type !== 'edge_add' && {
        label: 'Request',
        value: 'Request',
        metadata: { description: 'Trigger the workflow with a HTTP request.', color: colors.green, group: 'Trigger' },
      },
    !trigger &&
      contextMenu?.type !== 'edge_add' && {
        label: 'Inbound Call',
        value: 'InboundCall',
        metadata: { description: 'Trigger the workflow with an inbound call.', color: colors.green, group: 'Trigger' },
      },
    !trigger &&
      contextMenu?.type !== 'edge_add' && {
        label: 'Inbound Message',
        value: 'InboundMessage',
        metadata: {
          description: 'Trigger the workflow with an inbound message.',
          color: colors.green,
          group: 'Trigger',
        },
      },
    !trigger &&
      !hasModuleNode &&
      contextMenu?.type !== 'edge_add' && {
        label: 'Module',
        value: 'Module',
        metadata: {
          description: 'Trigger this workflow from a node.',
          color: colors.green,
          group: 'Trigger',
        },
      },
    {
      label: 'Response',
      value: 'Response',
      metadata: { description: 'Return a response to the request trigger.', color: colors.green, group: 'Outputs' },
    },
    isTelnyxUser && {
      label: 'AI Assistant',
      value: 'Assistant',
      metadata: {
        description: 'Generate text completions using an AI Assistant.',
        color: colors.blue,
        group: 'Large Language Models',
      },
    },
    isTelnyxUser && {
      label: 'AI Assistant Router',
      value: 'AssistantRouter',
      metadata: {
        description: 'Route request to many assistants.',
        color: colors.blue,
        group: 'Large Language Models',
      },
    },
    {
      label: 'AI Completion',
      value: 'Completion',
      metadata: {
        description: 'Generate text completions.',
        color: colors.blue,
        group: 'Large Language Models',
      },
    },
    contextMenu?.type !== 'edge_add' && {
      label: 'AI Tool',
      value: 'Tool',
      metadata: {
        description: 'Add tools to completion nodes.',
        color: colors.blue,
        group: 'Large Language Models',
      },
    },
    isTelnyxUser && {
      label: 'Store Message',
      value: 'StoreMessage',
      metadata: {
        description: 'Store a message pair to your LLM conversation history.',
        color: colors.blue,
        group: 'Large Language Models',
      },
    },
    {
      label: 'Delay',
      value: 'Delay',
      metadata: { description: 'Delay the execution of the workflow.', color: colors.red, group: 'Utility' },
    },
    {
      label: 'Base64 Transformer',
      value: 'Base64Transformer',
      metadata: {
        description: 'Encode or decode a base64 string.',
        color: colors.red,
        group: 'Utility',
      },
    },
    {
      label: 'Datetime',
      value: 'DateTime',
      metadata: {
        description: 'Get the current date and time.',
        color: colors.red,
        group: 'Utility',
      },
    },
    {
      label: 'Jump To',
      value: 'JumpTo',
      metadata: {
        description: 'Jump to a target node and execute it. Used to re-execute nodes.',
        color: colors.orange,
        group: 'Logic',
      },
    },
    {
      label: 'Branch',
      value: 'Branch',
      metadata: {
        description: 'Conditionally execute on a condition.',
        color: colors.orange,
        group: 'Logic',
      },
    },
    {
      label: 'Switch',
      value: 'Switch',
      metadata: {
        description: 'Conditionally execute on MANY conditions.',
        color: colors.orange,
        group: 'Logic',
      },
    },
    {
      label: 'HTTP Client',
      value: 'HTTPClient',
      metadata: { description: 'Send a HTTP request.', color: colors.purple, group: 'Clients' },
    },
    {
      label: 'Send Message',
      value: 'SendMessage',
      metadata: {
        description: 'Send a message with a Phone Number, Alphanumeric Sender ID, Short Code or Number Pool.',
        color: themeType === 'dark' ? theme.telnyx.citron : theme.text.standard,
        group: 'Messaging',
      },
    },
    {
      label: 'Answer Call',
      value: 'AnswerCall',
      metadata: {
        description: 'Answer an incoming call. Must be issued before executing subsequent commands.',
        color: themeType === 'dark' ? theme.telnyx.citron : theme.text.standard,
        group: 'Call Control',
      },
    },
    {
      label: 'Bridge',
      value: 'Bridge',
      metadata: {
        description: 'Bridge two call control calls.',
        color: themeType === 'dark' ? theme.telnyx.citron : theme.text.standard,
        group: 'Call Control',
      },
    },
    {
      label: 'Dial',
      value: 'Dial',
      metadata: {
        description: 'Dial a number or SIP URI from a given connection.',
        color: themeType === 'dark' ? theme.telnyx.citron : theme.text.standard,
        group: 'Call Control',
      },
    },
    {
      label: 'Speak Text',
      value: 'SpeakText',
      metadata: {
        description: 'Convert text to speech and play it back on the call.',
        color: themeType === 'dark' ? theme.telnyx.citron : theme.text.standard,
        group: 'Call Control',
      },
    },
    {
      label: 'Transfer Call',
      value: 'TransferCall',
      metadata: {
        description: 'Transfer a call to a new destination.',
        color: themeType === 'dark' ? theme.telnyx.citron : theme.text.standard,
        group: 'Call Control',
      },
    },
    {
      label: 'Transcription Start',
      value: 'TranscriptionStart',
      metadata: {
        description: 'Start real-time transcription. Transcription will stop on call hang-up or via command.',
        color: themeType === 'dark' ? theme.telnyx.citron : theme.text.standard,
        group: 'Call Control',
      },
    },
    {
      label: 'Transcription Stop',
      value: 'TranscriptionStop',
      metadata: {
        description: 'Stop real-time transcription.',
        color: themeType === 'dark' ? theme.telnyx.citron : theme.text.standard,
        group: 'Call Control',
      },
    },
    {
      label: 'Gather Using AI',
      value: 'GatherUsingAI',
      metadata: {
        description: 'Gather parameters defined in the request payload using a voice assistant.',
        color: themeType === 'dark' ? theme.telnyx.citron : theme.text.standard,
        group: 'Call Control',
      },
    },
    {
      label: 'Playback Start',
      value: 'PlaybackStart',
      metadata: {
        description: 'Play an audio file on the call.',
        color: themeType === 'dark' ? theme.telnyx.citron : theme.text.standard,
        group: 'Call Control',
      },
    },
    {
      label: 'Playback Stop',
      value: 'PlaybackStop',
      metadata: {
        description: 'Stop audio/speech being played on the call.',
        color: themeType === 'dark' ? theme.telnyx.citron : theme.text.standard,
        group: 'Call Control',
      },
    },
    {
      label: 'Recording Start',
      value: 'RecordingStart',
      metadata: {
        description: 'Start recording the call.',
        color: themeType === 'dark' ? theme.telnyx.citron : theme.text.standard,
        group: 'Call Control',
      },
    },
    {
      label: 'Recording Stop',
      value: 'RecordingStop',
      metadata: {
        description: 'Stop recording the call.',
        color: themeType === 'dark' ? theme.telnyx.citron : theme.text.standard,
        group: 'Call Control',
      },
    },
    {
      label: 'Recording Pause',
      value: 'RecordingPause',
      metadata: {
        description: 'Pause recording the call.',
        color: themeType === 'dark' ? theme.telnyx.citron : theme.text.standard,
        group: 'Call Control',
      },
    },
    {
      label: 'Recording Resume',
      value: 'RecordingResume',
      metadata: {
        description: 'Resume recording the call.',
        color: themeType === 'dark' ? theme.telnyx.citron : theme.text.standard,
        group: 'Call Control',
      },
    },
    {
      label: 'Streaming Start',
      value: 'StreamingStart',
      metadata: {
        description: 'Start streaming the media from a call.',
        color: themeType === 'dark' ? theme.telnyx.citron : theme.text.standard,
        group: 'Call Control',
      },
    },
    {
      label: 'Streaming Stop',
      value: 'StreamingStop',
      metadata: {
        description: 'Stop streaming the media from a call.',
        color: themeType === 'dark' ? theme.telnyx.citron : theme.text.standard,
        group: 'Call Control',
      },
    },
    {
      label: 'Noise Suppression',
      value: 'NoiseSuppression',
      metadata: {
        description: 'Suppress audio for certain or both directions on the phone call.',
        color: themeType === 'dark' ? theme.telnyx.citron : theme.text.standard,
        group: 'Call Control',
      },
    },
    {
      label: 'Stop Noise Suppression',
      value: 'NoiseSuppressionStop',
      metadata: {
        description: 'Stop suppressing audio on the phone call.',
        color: themeType === 'dark' ? theme.telnyx.citron : theme.text.standard,
        group: 'Call Control',
      },
    },
    {
      label: 'Reject Call',
      value: 'RejectCall',
      metadata: {
        description: 'Reject an incoming call.',
        color: themeType === 'dark' ? theme.telnyx.citron : theme.text.standard,
        group: 'Call Control',
      },
    },
    {
      label: 'Hangup Call',
      value: 'HangupCall',
      metadata: {
        description: 'Terminate a call control call.',
        color: themeType === 'dark' ? theme.telnyx.citron : theme.text.standard,
        group: 'Call Control',
      },
    },
    ...(trigger?.config.command_id === 'workflow.module'
      ? []
      : modules.map((mod: any) => ({
          label: mod.name,
          value: 'ModNode',
          module_id: mod.flow_id,
          metadata: { description: mod?.description || 'No description.', color: colors.yellow, group: 'Modules' },
        }))),
  ];

  const filteredOptions = options.filter(Boolean) as ListItemType[];

  const groups = Array.from(new Set(filteredOptions.map((opt) => opt.metadata?.group || '')));

  const sortedOptions = groups.flatMap((group) =>
    filteredOptions
      .filter((opt) => (opt.metadata?.group || '') === group)
      .sort((a, b) => a.label.localeCompare(b.label))
  );

  return (
    <MUIMenu
      open={contextMenu !== null}
      anchorReference='anchorPosition'
      anchorPosition={anchorPosition}
      sx={{
        '& .MuiPaper-root': { border: 0 },
        '& .MuiInputLabel-root': { overflow: 'visible', height: '20px' },
      }}
      onClose={() => {
        setContextMenu(null);
        setConnector(null);
      }}
    >
      <NodesAutocomplete
        options={sortedOptions}
        onChange={(_, newValue) => {
          addNode({ type: newValue.value, position, connectData: true, moduleId: newValue?.module_id || null });
          setContextMenu(null);
        }}
        placeholder='Add a node'
        contextMenu={contextMenu}
        setContextMenu={setContextMenu}
      />
    </MUIMenu>
  );
};
